'use client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Language, SUPPORTED_LANGUAGES, getDirection, isRtlLocale } from '@/lib/i18n';

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  direction: 'rtl' | 'ltr';
  isRtl: boolean;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: 'en',
  setLanguage: () => {},
  direction: 'ltr',
  isRtl: false,
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>('en');

  // Hydrate from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('muyulink_language') as Language | null;
      const valid = SUPPORTED_LANGUAGES.some((l) => l.code === stored);
      if (stored && valid) {
        setLanguageState(stored);
      }
    } catch {
      // localStorage unavailable (SSR / private browsing)
    }
  }, []);

  // Sync direction on <html> element whenever language changes
  useEffect(() => {
    const dir = getDirection(language);
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', language);
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem('muyulink_language', lang);
    } catch {
      // ignore
    }
  }, []);

  const direction = getDirection(language);
  const isRtl = isRtlLocale(language);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, direction, isRtl }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  return useContext(LanguageContext);
}
